import { displayName } from '../../types';
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Button, LinearProgress,
  Chip, Alert, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, List, ListItem, ListItemText,
  IconButton, Checkbox, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { apiService } from '../../services/api';

const Onboarding: React.FC = () => {
  const [onboardings, setOnboardings] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newOnboardingOpen, setNewOnboardingOpen] = useState(false);
  const [newTemplateOpen, setNewTemplateOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [templateTasks, setTemplateTasks] = useState([{ title: '', dueOffsetDays: 0 }]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ob, tmpl, emps] = await Promise.all([
        apiService.getEmployeeOnboardings().catch(() => []),
        apiService.getOnboardingTemplates().catch(() => []),
        apiService.getEmployees().catch(() => []),
      ]);
      setOnboardings(ob);
      setTemplates(tmpl);
      setEmployees(emps);
    } catch (err) {
      setError('Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleStartOnboarding = async () => {
    try {
      await apiService.createEmployeeOnboarding({ employeeId: selectedEmployee, templateId: selectedTemplate, startDate });
      setNewOnboardingOpen(false);
      fetchData();
    } catch { setError('Failed to start onboarding'); }
  };

  const handleCreateTemplate = async () => {
    try {
      await apiService.createOnboardingTemplate({
        name: templateName, description: templateDesc,
        tasks: templateTasks.filter(t => t.title),
      });
      setNewTemplateOpen(false);
      setTemplateName(''); setTemplateDesc('');
      setTemplateTasks([{ title: '', dueOffsetDays: 0 }]);
      fetchData();
    } catch { setError('Failed to create template'); }
  };

  const handleToggleTask = async (taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    await apiService.updateOnboardingTask(taskId, { status: newStatus });
    fetchData();
  };

  const getProgress = (onboarding: any) => {
    if (!onboarding.tasks?.length) return 0;
    const done = onboarding.tasks.filter((t: any) => t.status === 'completed' || t.status === 'skipped').length;
    return Math.round((done / onboarding.tasks.length) * 100);
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent={{ xs: "column", sm: "space-between" }} alignItems={{ xs: "flex-start", sm: "center" }} gap={1} mb={3}>
        <Typography variant="h5" fontWeight="bold">Onboarding</Typography>
        <Box display="flex" gap={1}>
          <Button variant="outlined" onClick={() => setNewTemplateOpen(true)}>+ Template</Button>
          <Button variant="contained" onClick={() => setNewOnboardingOpen(true)} startIcon={<Add />}>Start Onboarding</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Active Onboardings */}
      <Typography variant="h6" gutterBottom>Active Onboardings ({onboardings.filter(o => o.status === 'in_progress').length})</Typography>
      {onboardings.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No onboardings yet. Start one for a new employee!</Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {onboardings.map((ob: any) => {
            const progress = getProgress(ob);
            return (
              <Grid size={{ xs: 12, md: 6 }} key={ob.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {displayName(ob.employee)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ob.employee?.department?.name} · Started {new Date(ob.startDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Chip
                        label={ob.status === 'completed' ? '✅ Done' : `${progress}%`}
                        color={ob.status === 'completed' ? 'success' : 'primary'}
                        size="small"
                      />
                    </Box>
                    <LinearProgress variant="determinate" value={progress} sx={{ mb: 2, borderRadius: 1 }} />
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                      Template: {ob.template?.name}
                    </Typography>
                    <List dense disablePadding>
                      {ob.tasks?.map((task: any) => (
                        <ListItem key={task.id} disablePadding sx={{ py: 0.25 }}>
                          <Checkbox
                            size="small"
                            checked={task.status === 'completed'}
                            onChange={() => handleToggleTask(task.id, task.status)}
                            sx={{ p: 0.5 }}
                          />
                          <ListItemText
                            primary={task.templateTask?.title}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: { textDecoration: task.status === 'completed' ? 'line-through' : 'none', color: task.status === 'completed' ? 'text.secondary' : 'text.primary' }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Templates section */}
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>Templates ({templates.length})</Typography>
        <Grid container spacing={2}>
          {templates.map((t: any) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={t.id}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight="bold">{t.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{t.description}</Typography>
                  <Typography variant="body2" mt={1}>{t.tasks?.length || 0} tasks</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Start Onboarding Dialog */}
      <Dialog open={newOnboardingOpen} onClose={() => setNewOnboardingOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Start Onboarding</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Employee</InputLabel>
            <Select value={selectedEmployee} onChange={e => setSelectedEmployee(e.target.value)} label="Employee">
              {employees.map((e: any) => <MenuItem key={e.id} value={e.id}>{displayName(e)}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Template</InputLabel>
            <Select value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)} label="Template">
              {templates.map((t: any) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Start Date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOnboardingOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleStartOnboarding}>Start</Button>
        </DialogActions>
      </Dialog>

      {/* Create Template Dialog */}
      <Dialog open={newTemplateOpen} onClose={() => setNewTemplateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Onboarding Template</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Template Name" value={templateName} onChange={e => setTemplateName(e.target.value)} fullWidth />
          <TextField label="Description" value={templateDesc} onChange={e => setTemplateDesc(e.target.value)} fullWidth multiline rows={2} />
          <Typography variant="subtitle2">Tasks</Typography>
          {templateTasks.map((task, i) => (
            <Box key={i} display="flex" gap={1}>
              <TextField label={`Task ${i + 1}`} value={task.title} onChange={e => {
                const updated = [...templateTasks]; updated[i] = { ...updated[i], title: e.target.value }; setTemplateTasks(updated);
              }} fullWidth size="small" />
              <TextField label="Days" type="number" value={task.dueOffsetDays} onChange={e => {
                const updated = [...templateTasks]; updated[i] = { ...updated[i], dueOffsetDays: parseInt(e.target.value) }; setTemplateTasks(updated);
              }} sx={{ width: 80 }} size="small" />
            </Box>
          ))}
          <Button onClick={() => setTemplateTasks([...templateTasks, { title: '', dueOffsetDays: 0 }])} size="small">+ Add Task</Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewTemplateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateTemplate}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Onboarding;
